import { useRef } from 'react';
import { motion, useMotionValue, useReducedMotion, useScroll, useTransform } from 'framer-motion';

// Every animation on the landing page is a pure function of scroll position:
// no timers, no in-view triggers, no randomness. Scrolling to the same spot
// always renders the same frame, which keeps screen recordings repeatable.

/** Maps progress p to 0..1 across [start, end], clamped. */
export function useSpan(p, start, end) {
  return useTransform(p, [start, end], [0, 1], { clamp: true });
}

/**
 * A pinned scene. The section is `length` viewports tall and its content
 * sticks to the viewport while `progress` runs 0 -> 1. With reduced motion
 * the scene is a normal block and progress is fixed at 1 (final state).
 */
export function Scene({ id, length = 2.5, className = '', label, children }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });
  const settled = useMotionValue(1);
  const progress = reduce ? settled : scrollYProgress;

  return (
    <section
      id={id}
      ref={ref}
      aria-label={label}
      className="relative"
      style={reduce ? undefined : { height: `${length * 100}vh` }}
    >
      <div className={`${reduce ? 'py-24' : 'sticky top-0 h-[100svh] overflow-hidden'} ${className}`}>
        {children(progress)}
      </div>
    </section>
  );
}

/**
 * Scroll-linked reveal for unpinned content: fades and lifts as the element
 * travels from the bottom of the viewport to 70% of the way up.
 */
export function Reveal({ children, className = '', delay = 0, as = 'div' }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'start 70%'] });
  const t = useTransform(scrollYProgress, [delay, Math.min(1, delay + 0.7)], [0, 1], { clamp: true });
  const opacity = useTransform(t, [0, 1], [0, 1]);
  const y = useTransform(t, [0, 1], [48, 0]);
  const Tag = motion[as];

  return (
    <Tag ref={ref} className={className} style={reduce ? undefined : { opacity, y }}>
      {children}
    </Tag>
  );
}

/** Renders a motion value as formatted text without re-rendering React. */
export function Counter({ value, format = v => v.toFixed(1), className = '' }) {
  const text = useTransform(value, format);
  return <motion.span className={className}>{text}</motion.span>;
}
